"use client";

import { dataLayer } from "@repo/analytics/data-layer-utils";
import { componentNameConstants, dlElementConstants, dlEventConstants } from "@repo/analytics/data-layer.constants";
import BFLButton from "@repo/shared-ui/BFLButton";
import BFLModal from "@repo/shared-ui/BFLModal";
import useBodyScrollLock from "@repo/shared-ui/useBodyScrollLock";
import useModalCloseOnBrowserButton from "@repo/shared-ui/useModalCloseOnBrowserButton";
import { useRouter, useSearchParams } from "next/navigation";
import { ComponentProps, FC, memo, Suspense, useEffect, useRef, useState } from "react";
import { Chevron } from "../../../components/News/components/FilterDropdownButton";
import type { StockListingUILabels } from "../../../interfaces/StockListingTableAEM.interface";
import { useStockListingFilters } from "../context/StockListingFiltersContext";

const MOBILE_SORT_CHEVRON_ICON_CLASS =
  "inline-flex h-[11px] w-[11px] items-center justify-center overflow-hidden leading-none icomoon transition-transform before:inline-block before:font-black before:[-webkit-text-stroke:0.5px_currentColor] before:h-[11px] before:w-[11px] before:leading-[11px] before:text-[11px] before:content-['\\e909']";

const MOBILE_SORT_CLOSE_ICON_CLASS =
  "icomoon inline-flex h-[12px] w-[12px] items-center justify-center overflow-hidden leading-none text-[rgb(255,103,0)] before:inline-block before:h-[12px] before:w-[12px] before:leading-[12px] before:text-[12px] before:content-['\\e914'] md:before:content-['\\e914']";

export interface SortOption {
  value: string;
  label: string;
}

export interface StockSortDropdownProps {
  /** Current sort value */
  sortValue: string;
  /** Available sort options */
  sortOptions: SortOption[];
  /** Variant: "desktop" or "mobile" */
  variant?: "desktop" | "mobile";
  /** AEM UI labels */
  uiLabels?: StockListingUILabels;
}

/**
 * Sort dropdown component for stock listing pages.
 * Handles sort selection and URL updates.
 */
const StockSortDropdown: FC<StockSortDropdownProps> = memo(
  ({ sortValue, sortOptions, variant = "desktop", uiLabels = {} }) => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { activeSortValue, setActiveSortValue } = useStockListingFilters();
    const [isSortOpen, setIsSortOpen] = useState(false);
    const sortDropdownRef = useRef<HTMLDivElement>(null);
    const [selectedSort, setSelectedSort] = useState(sortValue);
    const [tempSelectedSort, setTempSelectedSort] = useState(sortValue); // Temporary selection for mobile modal

    const selectedSortLabel =
      sortOptions.find((opt) => opt.value === selectedSort)?.label ?? sortOptions[0]?.label ?? "";

    const isMobile = variant === "mobile";
    useBodyScrollLock(isSortOpen && !isMobile);

    // Sync with URL params
    useEffect(() => {
      const currentSort = activeSortValue ?? searchParams.get("sort") ?? sortValue;
      if (currentSort !== selectedSort) {
        setSelectedSort(currentSort);
        setTempSelectedSort(currentSort);
      }
    }, [activeSortValue, searchParams, sortValue, selectedSort]);

    // Reset temp selection when modal opens
    useEffect(() => {
      if (isSortOpen && isMobile) {
        setTempSelectedSort(selectedSort);
      }
    }, [isSortOpen, isMobile, selectedSort]);

    // Close dropdown on outside click (desktop only)
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const clickedInDropdown = sortDropdownRef.current?.contains(target);
        if (!clickedInDropdown) {
          setIsSortOpen(false);
        }
      };

      if (isSortOpen && !isMobile) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isSortOpen, isMobile]);

    const fireSortDL = (
      element: string,
      eventType: string,
      cleverTapExtras: Record<string, string>,
      sectionTitle?: string,
    ) => {
      const dlData: Record<string, string> = {
        component: componentNameConstants.STOCKS_SORT_BY,
        event: eventType,
        element,
        ...(sectionTitle !== undefined ? { section_title: sectionTitle } : {}),
      };
      const cleverTapObj = {
        ...cleverTapExtras,
      };
      dataLayer("", dlData, cleverTapObj);
    };

    const handleSortChange = (value: string) => {
      if (isMobile) {
        setTempSelectedSort(value);
      } else {
        const label = sortOptions.find((o) => o.value === value)?.label ?? value;
        fireSortDL(
          dlElementConstants.STOCKS_SORT_BY_DROPDOWN_CLICK,
          dlEventConstants.ICON_CLICK,
          { EP_SORT_BY: label, EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_STOCK_PLP },
          label,
        );
        setSelectedSort(value);
        setActiveSortValue?.(value);
        setIsSortOpen(false);
        const params = new URLSearchParams(searchParams.toString());
        params.set("sort", value);
        router.replace(`?${params.toString()}`, { scroll: false });
      }
    };

    const handleApply = () => {
      const applyText = uiLabels.applyText ?? "APPLY";
      fireSortDL(
        dlElementConstants.STOCKS_SORT_BY_APPLY_CLICK,
        dlEventConstants.BUTTON_CLICK,
        { EP_CTA: applyText, EP_SORT_BY: tempSelectedSort, EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_STOCK_PLP },
        applyText,
      );
      setSelectedSort(tempSelectedSort);
      setActiveSortValue?.(tempSelectedSort);
      setIsSortOpen(false);
      const params = new URLSearchParams(searchParams.toString());
      params.set("sort", tempSelectedSort);
      router.replace(`?${params.toString()}`, { scroll: false });
    };

    const handleCancel = () => {
      const cancelText = uiLabels.cancelText ?? "CANCEL";
      fireSortDL(
        dlElementConstants.STOCKS_SORT_BY_CANCEL_CLICK,
        dlEventConstants.BUTTON_CLICK,
        { EP_CTA: cancelText, EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_STOCK_PLP },
        cancelText,
      );
      setTempSelectedSort(selectedSort);
      setIsSortOpen(false);
    };

    const handleClose = () => {
      setTempSelectedSort(selectedSort); // Reset to current selection
      setIsSortOpen(false);
    };

    useModalCloseOnBrowserButton(isSortOpen && isMobile, handleClose);

    if (isMobile) {
      return (
        <>
          <div className="relative shrink-0 border-l border-[rgba(0,0,0,0.12)]" ref={sortDropdownRef}>
            <button
              type="button"
              onClick={() => {
                fireSortDL(
                  dlElementConstants.STOCKS_SORT_BY_ICON_CLICK,
                  dlEventConstants.ICON_CLICK,
                  {},
                  uiLabels.sortByLabel ?? "Sort by",
                );
                setIsSortOpen(!isSortOpen);
              }}
              className="box-border flex h-full items-center gap-2 p-4 text-[12px] md:text-sm text-[rgb(255,103,0)]"
              aria-expanded={isSortOpen}
              aria-haspopup="dialog"
            >
              <span className="font-medium">{uiLabels.sortByLabel ?? "Sort by"}</span>
              <i
                className={`${MOBILE_SORT_CHEVRON_ICON_CLASS} ${isSortOpen ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>
          </div>
          {isSortOpen && (
            <BFLModal
              isOpen={isSortOpen}
              onClose={handleClose}
              title={uiLabels.sortByLabel ?? "Sort by"}
              showHeader={false}
              enableScroll
              modalClassName="pointer-events-none items-end justify-center md:hidden"
              contentClassName="max-h-[85vh] rounded-t-lg"
              bodyClassName="pointer-events-auto"
              footerClassName="hidden"
            >
              {/* Custom radio button styles */}
              <style
                 
                dangerouslySetInnerHTML={{
                  __html: `
                    input[type="radio"].custom-radio {
                      appearance: none;
                      -webkit-appearance: none;
                      width: 16px;
                      height: 16px;
                      border: 2px solid #9ca3af;
                      border-radius: 50%;
                      outline: none;
                      cursor: pointer;
                      position: relative;
                    }
                    input[type="radio"].custom-radio:checked {
                      border-color: rgb(255, 103, 0);
                    }
                    input[type="radio"].custom-radio:checked::before {
                      content: "";
                      position: absolute;
                      top: 50%;
                      left: 50%;
                      transform: translate(-50%, -50%);
                      width: 8px;
                      height: 8px;
                      border-radius: 50%;
                      background-color: rgb(255, 103, 0);
                    }
                  `,
                }}
              />
              <div className="flex flex-col gap-3 pb-6 pt-3">
                <div className="flex flex-col gap-1">
                  {/* Close button */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-gray-100"
                      aria-label={uiLabels.closeAriaLabel ?? "Close"}
                    >
                      <i className={MOBILE_SORT_CLOSE_ICON_CLASS} aria-hidden="true" />
                    </button>
                  </div>
                  <h2
                    id="sort-modal-title"
                    className="text-center text-lg font-medium text-[#1A1A1A]"
                  >
                    {uiLabels.sortByLabel ?? "Sort by"}
                  </h2>
                  <p className="text-center text-sm text-gray-600">
                    {uiLabels.sortDescription ?? "Sort the list of stocks by selecting any one of the param listed below"}
                  </p>
                </div>
                <div className="flex flex-col gap-1 space-y-2">
                  {sortOptions.map((option) => (
                    <label
                      key={option.value}
                      className="flex cursor-pointer items-center gap-[15px]"
                    >
                      <input
                        type="radio"
                        name="sort-option"
                        value={option.value}
                        checked={tempSelectedSort === option.value}
                        onChange={() => handleSortChange(option.value)}
                        className="custom-radio"
                        aria-label={option.label}
                      />
                      <span
                        className={`text-[12px] text-[#002953] ${tempSelectedSort === option.value ? "font-medium" : ""}`}
                      >
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
                {/* Action buttons */}
                <div className="flex gap-3 px-[5.5px] pt-2">
                  <div className="flex-1">
                    <BFLButton
                      type="button"
                      onClick={handleCancel}
                      className="box-border h-[40px] w-full rounded-[200px] border-[1.5px] border-[rgb(255,103,0)] bg-white px-[24px] py-[12px] text-sm font-medium leading-[18px] text-[rgb(255,103,0)] hover:bg-[rgba(255,103,0,0.05)]"
                    >
                      {uiLabels.cancelText ?? "CANCEL"}
                    </BFLButton>
                  </div>
                  <div className="flex-1">
                    <BFLButton
                      type="button"
                      onClick={handleApply}
                      className="box-border h-[40px] w-full rounded-[200px] bg-[rgb(255,103,0)] px-[24px] py-[12px] text-sm font-medium leading-[18px] text-white hover:opacity-90"
                    >
                      {uiLabels.applyText ?? "APPLY"}
                    </BFLButton>
                  </div>
                </div>
              </div>
            </BFLModal>
          )}
        </>
      );
    }

    return (
      <div className="relative shrink-0" ref={sortDropdownRef}>
        <button
          type="button"
          onClick={() => {
            fireSortDL(
              dlElementConstants.STOCKS_SORT_BY_DROPDOWN_CLICK,
              dlEventConstants.ICON_CLICK,
              { EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_STOCK_PLP },
            );
            setIsSortOpen(!isSortOpen);
          }}
          className="box-border relative flex h-[28px] min-w-[200.398px] cursor-pointer items-center gap-1.5 rounded border border-[rgb(204,204,204)] bg-[rgb(240,242,242)] px-2 py-1.5 leading-[19.2px] whitespace-nowrap shadow-[2px_2px_4px_#0006] [unicode-bidi:isolate]  "
          aria-expanded={isSortOpen}
          aria-haspopup="menu"
        >
          <span className="box-border inline-flex h-[19.2031px] cursor-pointer items-center gap-1 text-xs font-normal leading-[19.2px] whitespace-nowrap text-[#000000] [unicode-bidi:isolate]">
            <span>{(uiLabels.sortByPrefix ?? "Sort by:").trimEnd()}</span>
            <span>{selectedSortLabel}</span>
          </span>
          <Chevron rotated={isSortOpen} />
        </button>
        {isSortOpen && (
          <div
            className="absolute right-0 z-2 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-none"
            role="menu"
          >
            {sortOptions.map((option) => (
              <div key={option.value}>
                <button
                  type="button"
                  onClick={() => handleSortChange(option.value)}
                  className={`w-full py-2 px-3 text-left text-[12px] border-b border-[rgba(0,0,0,0.2)] cursor-pointer text-[#1A1A1A]`}
                  role="menuitem"
                  aria-current={selectedSort === option.value ? "true" : undefined}
                >
                  {option.label}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);

StockSortDropdown.displayName = "StockSortDropdown";

// useSearchParams() requires a Suspense boundary on statically prerendered routes.
const SuspendedStockSortDropdown = (
  props: ComponentProps<typeof StockSortDropdown>,
) => (
  <Suspense
    fallback={
      props.variant === "mobile" ? (
        <div className="relative shrink-0 border-l border-[rgba(0,0,0,0.12)]" aria-hidden="true">
          <span className="box-border flex h-full items-center gap-2 p-4 text-[12px] text-transparent md:text-sm">
            {props.uiLabels?.sortByLabel ?? "Sort by"}
          </span>
        </div>
      ) : (
        <div
          className="box-border h-7 min-w-[200.398px] shrink-0 animate-pulse rounded border border-[rgb(204,204,204)] bg-[rgb(240,242,242)]"
          aria-hidden="true"
        />
      )
    }
  >
    <StockSortDropdown {...props} />
  </Suspense>
);

export default SuspendedStockSortDropdown;
